// Utility to get/set a persistent anonymous client id (UUID v4) in localStorage
// Falls back to cookies if localStorage is unavailable. For SSR, returns null.

function generateUuidV4() {
  // RFC4122 version 4 compliant
  // Use crypto if available for better randomness
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  const getRandom = () =>
    typeof crypto !== 'undefined' && crypto.getRandomValues
      ? crypto.getRandomValues(new Uint8Array(1))[0]
      : Math.floor(Math.random() * 256)

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = getRandom() & 15
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

const STORAGE_KEY = 'yumnia.clientId'
const COOKIE_NAME = 'yumnia_client_id'

function readCookie(name) {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)')
  )
  return match ? decodeURIComponent(match[1]) : null
}

function writeCookie(name, value, days = 3650) {
  // ~10 years
  if (typeof document === 'undefined') return
  const d = new Date()
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000)
  const expires = 'expires=' + d.toUTCString()
  document.cookie = `${name}=${encodeURIComponent(value)}; ${expires}; path=/; SameSite=Lax`
}

export function getOrCreateClientId() {
  // Avoid in non-browser contexts
  if (typeof window === 'undefined') return null

  // Try localStorage
  try {
    const existing = window.localStorage.getItem(STORAGE_KEY)
    if (existing) return existing
  } catch (e) {
    // ignore
  }

  // Try cookie
  const fromCookie = readCookie(COOKIE_NAME)
  if (fromCookie) {
    // Try to mirror to localStorage for future fast reads
    try {
      window.localStorage.setItem(STORAGE_KEY, fromCookie)
    } catch (e) {
      /* ignore */
    }
    return fromCookie
  }

  // Create new
  const id = generateUuidV4()

  // Persist
  try {
    window.localStorage.setItem(STORAGE_KEY, id)
  } catch (e) {
    /* ignore */
  }
  writeCookie(COOKIE_NAME, id)

  return id
}

export function setClientId(id) {
  if (typeof window === 'undefined' || !id) return
  try {
    window.localStorage.setItem(STORAGE_KEY, id)
  } catch (e) {
    /* ignore */
  }
  writeCookie(COOKIE_NAME, id)
}

export function clearClientId() {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    /* ignore */
  }
  // Expire cookie
  if (typeof document !== 'undefined') {
    document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`
  }
}
